import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import {
  ArrowDown,
  CalendarDays,
  Clock3,
  Copy,
  FileText,
  FileSpreadsheet,
  Home,
  ExternalLink,
  Loader2,
  Lock,
  Mail,
  MessageSquare,
  Bot,
  Paperclip,
  PanelLeftClose,
  Plug,
  RotateCcw,
  Send,
  Slack,
  ShieldCheck,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  isActiveOsJobStatus,
  isActiveOsStatus,
  isSupportManagedAppRecovery,
  getAppStatusDescriptor,
  type AppStatusDescriptor,
  type OperatingSystemApp,
  type OperatingSystemJob,
  type OperatingSystemJobEvent,
} from "@/lib/operating-systems";
import { isAgenticUiAvailableHere, visibleWorkspaceSection } from "@/lib/agentic-ui-availability";
import {
  answeredProviderClarificationEventIds,
  answeredRouterClarificationDecisionIds,
  currentAgenticFlowActivity,
  displayEventContent,
  hasBlockingCreditEvent,
  isCreditEvent,
  isUserEvent,
  isVisibleActivityEvent,
  isAgenticFlowActivityEvent,
  isVisiblePostCompletionProviderUserMessage,
  latestWaitingProviderClarificationEvent,
  providerText,
  routerClarificationAssociationState,
  resolvedProviderDialogEventIds,
} from "./base44MessageVisibility";
import { isComposerSubmitShortcut } from "./composerKeyboard";
import {
  parseProviderDialogDescription,
  parseProviderUiDescription,
  type ProviderDialogDescription,
  type ProviderDialogFormValues,
  type ProviderUiDescription,
} from "./providerDialogDescription";
import { filesFromDrop, hasFileDragPayload } from "./attachmentDrag";
import { providerMessageRequestsReply } from "./providerMessageReply";
import { clearAppQuestionDraft, restoreActionableQuestionDraft, writeAppQuestionDraft } from "@/components/auth/appQuestionDrafts";
import { AgenticFlowsPanel } from "./AgenticFlowsPanel";
import { createTranscriptAutoFollow } from "./transcriptAutoFollow";
import type {
  AgenticFlowDebugDetail,
  AgenticFlowListResponse,
  AgenticFlowProductDetail,
} from "@/lib/agentic-flows";
import { documentAccept, recognizeAttachment, documentGroup } from "../../../supabase/functions/_shared/documentFormats";

const PANEL_BG = "#f7f8fb";
const COLLAPSED_MESSAGE_LENGTH = 104;
const CUSTOM_ANSWER_LABEL = "Write your own answer";
const ACTIVITY_PANEL_DEFAULT_WIDTH = 460;
const ACTIVITY_PANEL_MIN_WIDTH = 320;
const ACTIVITY_PANEL_MAX_WIDTH = 720;
const ACTIVITY_PANEL_MIN_PREVIEW_WIDTH = 420;
const ACTIVITY_PANEL_WIDTH_STORAGE_KEY = "applyt:activity-panel-width";
const sharedIntegrationInstruction = (service: string) =>
  `Connect this app to ${service} through one shared connection for the whole app. Before changing the app, inspect its entities, pages, and workflow, suggest the most relevant ways ${service} could support it, and ask me one concise question to choose how to continue. Wait for my answer, then configure the shared integration and implement the selected flow.`;

export function activityPanelMaximumWidth(viewportWidth: number): number {
  return Math.max(
    ACTIVITY_PANEL_MIN_WIDTH,
    Math.min(ACTIVITY_PANEL_MAX_WIDTH, viewportWidth - ACTIVITY_PANEL_MIN_PREVIEW_WIDTH),
  );
}

export function clampActivityPanelWidth(width: number, viewportWidth: number): number {
  return Math.min(
    activityPanelMaximumWidth(viewportWidth),
    Math.max(ACTIVITY_PANEL_MIN_WIDTH, Math.round(width)),
  );
}

export const SHARED_INTEGRATION_CATALOG: ReadonlyArray<{
  service: "Gmail" | "Google Calendar" | "Google Sheets" | "Google Docs" | "Slack";
  icon: LucideIcon;
  instruction: string;
}> = [
  { service: "Gmail", icon: Mail, instruction: sharedIntegrationInstruction("Gmail") },
  { service: "Google Calendar", icon: CalendarDays, instruction: sharedIntegrationInstruction("Google Calendar") },
  { service: "Google Sheets", icon: FileSpreadsheet, instruction: sharedIntegrationInstruction("Google Sheets") },
  { service: "Google Docs", icon: FileText, instruction: sharedIntegrationInstruction("Google Docs") },
  { service: "Slack", icon: Slack, instruction: sharedIntegrationInstruction("Slack") },
];

const SHARED_INTEGRATION_INSTRUCTIONS = new Set(
  SHARED_INTEGRATION_CATALOG.map(({ instruction }) => instruction),
);

type ClarifyingQuestionOption = {
  label: string;
  subtitle: string | null;
  custom: boolean;
};

type ClarifyingQuestion = {
  question: string;
  options: ClarifyingQuestionOption[];
  multiSelect?: boolean;
};

export type ProviderClarificationAnswer = {
  questionIndex: number;
  selectedLabel?: string;
  selectedLabels?: string[];
  customText?: string;
};

export type ProviderDialogAction = "approve" | "reject" | "begin_oauth" | "poll_oauth" | "submit_form";

type ProviderDialogProjection = ProviderDialogDescription;

type ProviderClarificationDraft = {
  selectedLabels: string[];
  customSelected: boolean;
  customText: string;
};

function hasProviderClarificationAnswer(draft: ProviderClarificationDraft): boolean {
  return draft.selectedLabels.length > 0 ||
    (draft.customSelected && !!draft.customText.trim());
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asString(value: unknown, maxLength = 2_000): string | null {
  return typeof value === "string" && value.trim() && value.trim().length <= maxLength
    ? value.trim()
    : null;
}

type VisibleAttachment = {
  name: string;
  size: number;
  contentType: string;
};

function safeAttachmentName(value: string): string {
  return Array.from(value, (character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127 || character === "/" || character === "\\" ? " " : character;
  }).join("").replace(/\s+/g, " ").trim().slice(0, 160);
}

function visibleAttachments(event: OperatingSystemJobEvent): VisibleAttachment[] {
  const rawAttachments = asRecord(event.public_metadata).attachments;
  if (!Array.isArray(rawAttachments)) return [];

  return rawAttachments.slice(0, 5).flatMap((rawAttachment) => {
    const attachment = asRecord(rawAttachment);
    const rawName = asString(attachment.name, 255);
    const name = rawName ? safeAttachmentName(rawName) : null;
    const size = attachment.size;
    const contentType = asString(attachment.content_type, 160)?.toLowerCase();
    if (!name || typeof size !== "number" || !Number.isFinite(size) || size <= 0 || size > 10 * 1024 * 1024) {
      return [];
    }
    if (!contentType || !/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/.test(contentType)) return [];
    return [{ name, size, contentType }];
  });
}

function attachmentTypeLabel(contentType: string, name?: string): string {
  const kind = name ? recognizeAttachment(name, contentType) : null;
  if (kind) return documentGroup(kind);
  if (contentType === "application/pdf") return "PDF";
  if (contentType.startsWith("image/")) return "Image";
  if (contentType.startsWith("text/")) return "Text";
  if (contentType.includes("spreadsheet") || contentType === "application/vnd.ms-excel") return "Spreadsheet";
  if (contentType.includes("wordprocessingml") || contentType === "application/msword") return "Document";
  if (contentType.includes("json")) return "JSON";
  return "File";
}

function attachmentSizeLabel(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function MessageAttachments({ event }: { event: OperatingSystemJobEvent }) {
  const attachments = visibleAttachments(event);
  if (attachments.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1.5" aria-label="Attached files">
      {attachments.map((attachment, index) => (
        <li key={`${attachment.name}-${attachment.size}-${index}`} className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-300/80 bg-white/70 px-2.5 py-2 text-xs text-slate-700">
          <FileText className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate font-medium" title={attachment.name}>{attachment.name}</span>
          <span className="shrink-0 text-slate-500">{attachmentTypeLabel(attachment.contentType, attachment.name)} · {attachmentSizeLabel(attachment.size)}</span>
        </li>
      ))}
    </ul>
  );
}

function routerDecisionId(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).router_decision_id, 128);
}

function routerClarificationOptions(event: OperatingSystemJobEvent): ClarifyingQuestionOption[] {
  const rawOptions = asRecord(event.public_metadata).options;
  const options = Array.isArray(rawOptions)
    ? rawOptions.slice(0, 6).flatMap((option) => {
      const label = asString(asRecord(option).label, 160);
      return label ? [{ label, subtitle: null, custom: label === CUSTOM_ANSWER_LABEL }] : [];
    })
    : [];
  const distinct = options.filter((option, index) =>
    options.findIndex((candidate) => candidate.label === option.label) === index,
  );

  return distinct.some((option) => option.custom || option.label === CUSTOM_ANSWER_LABEL)
    ? distinct
    : [...distinct, { label: CUSTOM_ANSWER_LABEL, subtitle: null, custom: true }];
}

function providerDialogState(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).dialog_state, 40);
}

function providerDialogKey(event: OperatingSystemJobEvent): string {
  return asString(asRecord(event.public_metadata).dialog_key, 128) ?? event.id;
}

function providerMessageResponseTargetId(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).provider_message_event_id, 128);
}

function providerMessageResponseState(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).submission_state, 40);
}

type ProviderEditSuggestion = {
  title: string;
  prompt: string;
};

type AgenticFlowSuggestion = {
  discoveryId: string | null;
  candidateId: string;
  shortName: string;
  whatItDoes: string;
  whyUseful: string;
  observes: string[];
  result: string;
  readiness: "ready_to_build" | "generatable" | "needs_data" | "needs_integration" | "needs_adapter";
  missingRequirement: string | null;
  selectionPrompt: string;
};

function agenticFlowSuggestions(event: OperatingSystemJobEvent): AgenticFlowSuggestion[] {
  const metadata = asRecord(event.public_metadata);
  const candidates = metadata.candidates;
  const discoveryId = asString(metadata.discovery_id, 36);
  if (!Array.isArray(candidates)) return [];
  return candidates.flatMap((raw) => {
    const candidate = asRecord(raw);
    const candidateId = asString(candidate.candidate_id, 80);
    const shortName = asString(candidate.short_name, 80);
    const whatItDoes = asString(candidate.what_it_does, 500);
    const whyUseful = asString(candidate.why_useful, 500);
    const result = asString(candidate.result, 500);
    const selectionPrompt = asString(candidate.selection_prompt, 300);
    const readiness = asString(candidate.readiness, 30);
    const observes = Array.isArray(candidate.observes)
      ? candidate.observes.flatMap((value) => asString(value, 200) ?? []).slice(0, 6)
      : [];
    const missingRequirement = candidate.missing_requirement === null
      ? null
      : asString(candidate.missing_requirement, 500);
    if (!candidateId || !shortName || !whatItDoes || !whyUseful || !result || !selectionPrompt || !observes.length || !["ready_to_build", "generatable", "needs_data", "needs_integration", "needs_adapter"].includes(readiness ?? "")) return [];
    return [{ discoveryId, candidateId, shortName, whatItDoes, whyUseful, observes, result, readiness: readiness as AgenticFlowSuggestion["readiness"], missingRequirement, selectionPrompt }];
  }).slice(0, 4);
}

function providerEditSuggestions(events: OperatingSystemJobEvent[]): ProviderEditSuggestion[] {
  const latestSuggestionEvent = [...events].reverse().find((event) =>
    event.source === "base44_editor" && event.event_type === "suggestions",
  );
  const rawSuggestions = asRecord(latestSuggestionEvent?.public_metadata).suggestions;
  // Older history rows predate the safe metadata projection. Their visible
  // labels are still sufficient to prefill the composer, so retain them as a
  // compatibility fallback without fetching the provider's raw response.
  if (!Array.isArray(rawSuggestions)) {
    return (latestSuggestionEvent?.content ?? "")
      .split(/\r?\n/)
      .map((title) => title.trim())
      .filter(Boolean)
      .slice(0, 4)
      .map((title) => ({ title, prompt: title }));
  }

  const seen = new Set<string>();
  return rawSuggestions.flatMap((item) => {
    const suggestion = asRecord(item);
    const title = asString(suggestion.title, 160);
    const prompt = asString(suggestion.prompt, 1_000);
    if (!title || !prompt || seen.has(prompt)) return [];
    seen.add(prompt);
    return [{ title, prompt }];
  }).slice(0, 4);
}

function repliedProviderMessageEventIds(events: OperatingSystemJobEvent[]): Set<string> {
  const repliedIds = new Set<string>();
  for (const event of events) {
    if (
      event.source !== "applyt" ||
      event.event_type !== "provider_message_response" ||
      !["queued", "submitting", "submitted"].includes(providerMessageResponseState(event) ?? "")
    ) {
      continue;
    }
    const targetId = providerMessageResponseTargetId(event);
    if (targetId) repliedIds.add(targetId);
  }
  return repliedIds;
}

function providerDialogProjection(event: OperatingSystemJobEvent): ProviderDialogProjection | null {
  return parseProviderDialogDescription(asRecord(event.public_metadata).dialog);
}

function providerUiProjection(event: OperatingSystemJobEvent): ProviderUiDescription | null {
  return parseProviderUiDescription(asRecord(event.public_metadata).provider_ui);
}

function providerMultiSelectQuestions(event: OperatingSystemJobEvent): boolean[] {
  const value = asRecord(event.public_metadata).multi_select_questions;
  return Array.isArray(value) ? value.map((item) => item === true) : [];
}

function providerClarificationContent(event: OperatingSystemJobEvent): string {
  return (event.content ?? "")
    .split(/\r?\n/)
    .map((line) => providerText(line))
    .join("\n")
    .trim();
}

function statusTone(status: string): string {
  if (status === "ready") return "bg-emerald-500";
  if (status === "update_failed") return "bg-amber-500";
  if (status === "failed") return "bg-red-500";
  if (status === "auth_blocked" || status === "update_auth_blocked") return "bg-amber-500";
  if (isActiveOsStatus(status)) return "bg-blue-500";
  return "bg-slate-400";
}

function formatEventTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const deltaMs = Date.now() - date.getTime();
  const minutes = Math.max(1, Math.round(deltaMs / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hours ago`;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
}

function ActivityTimestamp({ value }: { value?: string | null }) {
  const label = formatEventTime(value);
  if (!label) return null;
  return <div className="mb-2 text-xs text-slate-300">{label}</div>;
}

function toolRows(content: string) {
  return content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^(Read|Wrote|Deleted|Edited|Renamed)\s+(.+)$/i);
      return {
        action: match?.[1] ?? "Edited",
        target: match?.[2] ?? line,
      };
    });
}

function parseOption(line: string): ClarifyingQuestionOption | null {
  const optionText = line.replace(/^-\s*/, "").trim();
  if (!optionText) return null;
  if (optionText === CUSTOM_ANSWER_LABEL) {
    return {
      label: CUSTOM_ANSWER_LABEL,
      subtitle: null,
      custom: true,
    };
  }

  const separatorIndex = optionText.indexOf(": ");
  if (separatorIndex === -1) {
    return {
      label: optionText,
      subtitle: null,
      custom: false,
    };
  }

  return {
    label: optionText.slice(0, separatorIndex).trim(),
    subtitle: optionText.slice(separatorIndex + 2).trim() || null,
    custom: false,
  };
}

function parseClarifyingQuestions(content: string): ClarifyingQuestion[] {
  const questions: ClarifyingQuestion[] = [];
  let current: ClarifyingQuestion | null = null;

  for (const rawLine of content.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith("- ")) {
      const option = parseOption(line);
      if (current && option) current.options.push(option);
      continue;
    }

    if (current) questions.push(current);
    current = {
      question: line,
      options: [],
    };
  }

  if (current) questions.push(current);
  return questions.map((question) => ({
    ...question,
    options: question.options.length > 0
      ? question.options
      : [{ label: CUSTOM_ANSWER_LABEL, subtitle: null, custom: true }],
  }));
}

function answerInstruction(question: string, answer: string): string {
  return `Answer this workspace question:\n\n${question}\n\nAnswer: ${answer}`;
}

function skipInstruction(question: string): string {
  return `Skip this workspace question and proceed with the best default:\n\n${question}`;
}

export function groupWorkspaceActivityEvents(events: OperatingSystemJobEvent[]): OperatingSystemJobEvent[] {
  return events.reduce<OperatingSystemJobEvent[]>((grouped, event) => {
    const previous = grouped[grouped.length - 1];
    const closeInTime = previous
      && new Date(event.observed_at).getTime() - new Date(previous.observed_at).getTime() < 2 * 60 * 1000;
    if (event.event_type === "workspace_activity" && previous?.event_type === "workspace_activity" && closeInTime) {
      const rows = [...new Set(
        [previous.content, event.content]
          .filter((content): content is string => !!content)
          .flatMap((content) => content.split("\n"))
          .map((content) => content.trim())
          .filter(Boolean),
      )];
      grouped[grouped.length - 1] = {
        ...previous,
        content: rows.join("\n"),
      };
      return grouped;
    }
    grouped.push(event);
    return grouped;
  }, []);
}

function latestProviderDialogEvents(events: OperatingSystemJobEvent[]): OperatingSystemJobEvent[] {
  const latestByKey = new Map<string, OperatingSystemJobEvent>();
  for (const event of events) {
    if (event.event_type === "provider_dialog") {
      // History snapshots are append-only and `events` is source ordered.
      // The newest snapshot is authoritative regardless of its state label.
      latestByKey.set(providerDialogKey(event), event);
    }
  }
  return events.filter((event) =>
    event.event_type !== "provider_dialog" || latestByKey.get(providerDialogKey(event))?.id === event.id
  );
}

function eventSortRank(event: OperatingSystemJobEvent): number {
  if (event.event_type === "editor_message") return 0;
  if (event.event_type === "clarifying_questions" || event.event_type === "provider_dialog") return 1;
  if (event.event_type === "workspace_activity") return 2;
  return 2;
}

function eventSourceOrder(event: OperatingSystemJobEvent): number | null {
  const value = asRecord(event.public_metadata).source_order;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function MessageContent({
  content,
  collapsible,
  className = "pl-8",
}: {
  content: string;
  collapsible: boolean;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const truncated = collapsible && content.length > COLLAPSED_MESSAGE_LENGTH && !expanded;
  const collapsedExcerpt = content.slice(0, COLLAPSED_MESSAGE_LENGTH).replace(/\n\s*\n/g, "\n").trimEnd();
  const visibleContent = truncated ? `${collapsedExcerpt}...` : content;

  return (
    <div className={`whitespace-pre-wrap break-words text-sm leading-6 text-slate-950 ${className}`}>
      {visibleContent}
      {truncated && (
        <button
          type="button"
          className="ml-1 inline text-xs font-medium text-slate-500 hover:text-slate-800"
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      )}
    </div>
  );
}

function ProviderMarkdownContent({ content }: { content: string }) {
  return (
    <div className="max-w-none break-words text-sm leading-6 text-slate-950">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        skipHtml
        components={{
          h1: ({ children }) => <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h1>,
          h2: ({ children }) => <h2 className="mb-2 mt-3 text-sm font-semibold first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-2 mt-3 text-sm font-semibold first:mt-0">{children}</h3>,
          p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold text-slate-950">{children}</strong>,
          ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-6">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-6">{children}</ol>,
          li: ({ children }) => <li className="pl-1">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-slate-300 pl-3 text-slate-700">
              {children}
            </blockquote>
          ),
          pre: ({ children }) => (
            <pre className="my-2 max-w-full overflow-x-auto rounded-md bg-slate-100 p-2 text-xs leading-5">
              {children}
            </pre>
          ),
          code: ({ children }) => (
            <code className="break-words rounded bg-slate-100 px-1 py-0.5 text-xs">
              {children}
            </code>
          ),
          table: ({ children }) => (
            <table className="my-2 block max-w-full overflow-x-auto border-collapse text-xs">
              {children}
            </table>
          ),
          th: ({ children }) => <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left font-semibold">{children}</th>,
          td: ({ children }) => <td className="border border-slate-300 px-2 py-1 align-top">{children}</td>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-slate-700 underline underline-offset-2 hover:text-slate-950">
              {children}
            </a>
          ),
          img: ({ alt }) => alt ? <span>{alt}</span> : null,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function ClarifyingQuestionBlock({
  eventId,
  question,
  canApply,
  submitting,
  onAnswer,
  directAnswer = false,
}: {
  eventId: string;
  question: ClarifyingQuestion;
  canApply: boolean;
  submitting: boolean;
  onAnswer: (answer: string) => void;
  directAnswer?: boolean;
}) {
  const restoredDraft = restoreActionableQuestionDraft(eventId, canApply, (value) => {
    const record = value && typeof value === "object" ? value as Record<string, unknown> : null;
    return typeof record?.selectedIndex === "number" && typeof record.customAnswer === "string"
      ? { selectedIndex: record.selectedIndex, customAnswer: record.customAnswer }
      : null;
  });
  const [selectedIndex, setSelectedIndex] = useState(() => restoredDraft?.selectedIndex ?? 0);
  const [customAnswer, setCustomAnswer] = useState(() => restoredDraft?.customAnswer ?? "");
  const selectedOption = question.options[selectedIndex] ?? question.options[0];
  const answerText = selectedOption?.custom
    ? customAnswer.trim()
    : [selectedOption?.label, selectedOption?.subtitle].filter(Boolean).join(" - ");
  const disabled = submitting || !canApply;
  const sendDisabled = disabled || !answerText;

  useEffect(() => {
    if (canApply) writeAppQuestionDraft(eventId, { selectedIndex, customAnswer });
  }, [canApply, customAnswer, eventId, selectedIndex]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <p className="text-sm font-medium leading-5 text-slate-950">{question.question}</p>
      <div className="mt-3 space-y-2">
        {question.options.map((option, index) => {
          const inputId = `${eventId}-${index}`;
          return (
            <label
              key={inputId}
              htmlFor={inputId}
              className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
            >
              <input
                id={inputId}
                type="radio"
                name={eventId}
                className="mt-0.5 h-4 w-4"
                checked={selectedIndex === index}
                disabled={disabled}
                onChange={() => setSelectedIndex(index)}
              />
              <span className="min-w-0">
                <span className="block leading-5">{option.label}</span>
                {option.subtitle && <span className="block text-xs leading-5 text-slate-500">{option.subtitle}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {selectedOption?.custom && (
        <Textarea
          value={customAnswer}
          onChange={(event) => setCustomAnswer(event.target.value)}
          disabled={disabled}
          rows={2}
          placeholder="Write your answer"
          className="mt-3 min-h-[4.25rem] resize-none text-sm"
        />
      )}
      <div className="mt-3 flex justify-end gap-2">
        {directAnswer && submitting && (
          <span role="status" aria-live="polite" className="mr-auto self-center text-xs text-slate-600">
            Continuing automatic setup…
          </span>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onAnswer(directAnswer ? "Please proceed with your best judgment." : skipInstruction(question.question))}
        >
          Skip
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={sendDisabled}
          onClick={() => onAnswer(directAnswer ? answerText : answerInstruction(question.question, answerText))}
        >
          Send
          {submitting
            ? <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin" />
            : <Send className="ml-1.5 h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function RouterClarificationEvent({
  event,
  actionable,
  associationLoading,
  canApply,
  submitting,
  answered,
  onAnswer,
}: {
  event: OperatingSystemJobEvent;
  actionable: boolean;
  associationLoading: boolean;
  canApply: boolean;
  submitting: boolean;
  answered: boolean;
  onAnswer: (decisionId: string, answer: string) => Promise<boolean>;
}) {
  const [answerSubmitting, setAnswerSubmitting] = useState(false);
  const [answeredLocally, setAnsweredLocally] = useState(false);
  const answerInFlightRef = useRef(false);
  const decisionId = routerDecisionId(event);
  const content = displayEventContent(event);
  if (!decisionId || !content) {
    return content ? (
      <div key={event.id} className="px-4"><MessageContent content={content} collapsible={false} className="" /></div>
    ) : null;
  }

  const isAnswered = answered || answeredLocally;
  const submitAnswer = async (answer: string) => {
    if (isAnswered || answerInFlightRef.current) return;
    answerInFlightRef.current = true;
    setAnswerSubmitting(true);
    let accepted = false;
    try {
      accepted = await onAnswer(decisionId, answer);
      if (accepted) { clearAppQuestionDraft(event.id); setAnsweredLocally(true); }
    } catch {
      // The parent presents a retryable error; keep this card actionable.
    } finally {
      setAnswerSubmitting(false);
      if (!accepted) answerInFlightRef.current = false;
    }
  };

  return (
    <div key={event.id} id={`activity-event-${event.id}`} className="scroll-mt-3 px-4">
      <ActivityTimestamp value={event.observed_at} />
      {isAnswered ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-sm font-medium leading-5 text-slate-950">{content}</p>
          <p className="mt-2 text-xs leading-5 text-slate-600">Answered.</p>
        </div>
      ) : associationLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-sm font-medium leading-5 text-slate-950">{content}</p>
          <p className="mt-2 flex items-center gap-1.5 text-xs leading-5 text-slate-600" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Checking whether this setup question is still waiting for your answer…
          </p>
        </div>
      ) : !actionable ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-sm font-medium leading-5 text-slate-950">{content}</p>
          <p className="mt-2 text-xs leading-5 text-slate-600">This question belongs to an earlier App request.</p>
        </div>
      ) : (
        <ClarifyingQuestionBlock
          eventId={event.id}
          question={{ question: content, options: routerClarificationOptions(event) }}
          canApply={canApply}
          submitting={submitting || answerSubmitting}
          directAnswer
          onAnswer={(answer) => { void submitAnswer(answer); }}
        />
      )}
    </div>
  );
}

function ProviderClarifyingQuestionEvent({
  event,
  actionable,
  answered,
  submitting,
  onAnswer,
}: {
  event: OperatingSystemJobEvent;
  actionable: boolean;
  answered: boolean;
  submitting: boolean;
  onAnswer: (eventId: string, answers: ProviderClarificationAnswer[]) => Promise<boolean>;
}) {
  const content = providerClarificationContent(event);
  const multiSelectQuestions = providerMultiSelectQuestions(event);
  const questions = parseClarifyingQuestions(content).map((question, index) => ({
    ...question,
    multiSelect: multiSelectQuestions[index] === true,
  }));
  const [drafts, setDrafts] = useState<Record<number, ProviderClarificationDraft>>(() =>
    restoreActionableQuestionDraft(event.id, actionable, (value) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return null;
      const entries = Object.entries(value as Record<string, unknown>);
      if (!entries.every(([key, draft]) => /^\d+$/.test(key) && draft && typeof draft === "object")) return null;
      return Object.fromEntries(entries) as Record<number, ProviderClarificationDraft>;
    }) ?? {},
  );
  const [answerSubmitting, setAnswerSubmitting] = useState(false);
  const [answeredLocally, setAnsweredLocally] = useState(false);
  const answerInFlightRef = useRef(false);
  const isAnswered = answered || answeredLocally;
  const disabled = submitting || answerSubmitting || !actionable || isAnswered;
  const draftAt = (index: number): ProviderClarificationDraft => drafts[index] ?? {
    selectedLabels: [],
    customSelected: false,
    customText: "",
  };
  const updateDraft = (
    index: number,
    update: (draft: ProviderClarificationDraft) => ProviderClarificationDraft,
  ) => {
    setDrafts((current) => ({
      ...current,
      [index]: update(current[index] ?? {
        selectedLabels: [],
        customSelected: false,
        customText: "",
      }),
    }));
  };
  const buildAnswers = (): ProviderClarificationAnswer[] => questions.flatMap((question, index) => {
    const draft = draftAt(index);
    if (!hasProviderClarificationAnswer(draft)) return [];
    if (question.multiSelect) {
      return [{
        questionIndex: index,
        ...(draft.selectedLabels.length > 0 ? { selectedLabels: draft.selectedLabels } : {}),
        ...(draft.customSelected && draft.customText.trim()
          ? { customText: draft.customText.trim() }
          : {}),
      }];
    }
    return [draft.customSelected
      ? { questionIndex: index, customText: draft.customText.trim() }
      : { questionIndex: index, selectedLabel: draft.selectedLabels[0] }];
  });
  const answers = buildAnswers();
  const hasAnswer = answers.length > 0;
  useEffect(() => {
    if (actionable && !isAnswered) writeAppQuestionDraft(event.id, drafts);
  }, [actionable, drafts, event.id, isAnswered]);
  if (questions.length === 0) return null;
  const submitAnswer = async (answers: ProviderClarificationAnswer[]) => {
    if (isAnswered || !actionable || answerInFlightRef.current) return;
    answerInFlightRef.current = true;
    setAnswerSubmitting(true);
    let accepted = false;
    try {
      accepted = await onAnswer(event.id, answers);
      if (accepted) { clearAppQuestionDraft(event.id); setAnsweredLocally(true); }
    } catch {
      // The parent presents a retryable error; keep this card actionable.
    } finally {
      setAnswerSubmitting(false);
      if (!accepted) answerInFlightRef.current = false;
    }
  };

  return (
    <div key={event.id} id={`activity-event-${event.id}`} className="scroll-mt-3 px-4">
      <ActivityTimestamp value={event.observed_at} />
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        {questions.map((question, questionIndex) => {
          const draft = draftAt(questionIndex);
          const standardOptions = question.options.filter((option) => !option.custom);
          return (
            <fieldset
              key={`${event.id}-${questionIndex}`}
              className={questionIndex > 0 ? "mt-5 border-t border-slate-200 pt-4" : ""}
              disabled={disabled}
            >
              <legend className="text-sm font-medium leading-5 text-slate-950">
                {question.question}
              </legend>
              <div className="mt-3 space-y-2">
                {standardOptions.map((option, optionIndex) => {
                  const inputId = `${event.id}-${questionIndex}-${optionIndex}`;
                  const selected = draft.selectedLabels.includes(option.label);
                  return (
                    <label
                      key={inputId}
                      htmlFor={inputId}
                      className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-70"
                    >
                      <input
                        id={inputId}
                        type={question.multiSelect ? "checkbox" : "radio"}
                        name={`${event.id}-${questionIndex}`}
                        className="mt-0.5 h-4 w-4"
                        checked={selected}
                        onChange={() => updateDraft(questionIndex, (current) => ({
                          ...current,
                          selectedLabels: question.multiSelect
                            ? selected
                              ? current.selectedLabels.filter((label) => label !== option.label)
                              : [...current.selectedLabels, option.label]
                            : [option.label],
                          ...(!question.multiSelect
                            ? { customSelected: false, customText: "" }
                            : {}),
                        }))}
                      />
                      <span className="min-w-0">
                        <span className="block leading-5">{option.label}</span>
                        {option.subtitle && (
                          <span className="block text-xs leading-5 text-slate-500">{option.subtitle}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
                <label
                  htmlFor={`${event.id}-${questionIndex}-custom`}
                  className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-70"
                >
                  <input
                    id={`${event.id}-${questionIndex}-custom`}
                    type={question.multiSelect ? "checkbox" : "radio"}
                    name={`${event.id}-${questionIndex}`}
                    className="mt-0.5 h-4 w-4"
                    checked={draft.customSelected}
                    onChange={() => updateDraft(questionIndex, (current) => ({
                      ...current,
                      customSelected: !current.customSelected,
                      ...(!question.multiSelect ? { selectedLabels: [] } : {}),
                    }))}
                  />
                  <span className="leading-5">{CUSTOM_ANSWER_LABEL}</span>
                </label>
              </div>
              {draft.customSelected && (
                <Textarea
                  value={draft.customText}
                  onChange={(changeEvent) => updateDraft(questionIndex, (current) => ({
                    ...current,
                    customText: changeEvent.target.value,
                  }))}
                  maxLength={2_000}
                  rows={2}
                  placeholder="Write your answer"
                  aria-label={`Write your answer for: ${question.question}`}
                  className="mt-3 min-h-[4.25rem] resize-none text-sm"
                />
              )}
            </fieldset>
          );
        })}
        {isAnswered ? (
          <p className="mt-3 text-xs leading-5 text-slate-600">Answered.</p>
        ) : actionable ? (
          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => { void submitAnswer([]); }}
            >
              Skip
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={disabled || !hasAnswer}
              onClick={() => { void submitAnswer(answers); }}
            >
              {answerSubmitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Send
              {!answerSubmitting && <Send className="ml-1.5 h-3.5 w-3.5" />}
            </Button>
          </div>
        ) : (
          <p className="mt-3 text-xs leading-5 text-slate-600">This question is no longer awaiting an answer.</p>
        )}
      </div>
    </div>
  );
}

function ProviderConnectorFormEvent({
  event,
  dialog,
  disabled,
  resolved,
  loading,
  actionSubmitting,
  onConnect,
  onReject,
}: {
  event: OperatingSystemJobEvent;
  dialog: ProviderDialogDescription & { form: NonNullable<ProviderDialogDescription["form"]> };
  disabled: boolean;
  resolved: boolean;
  loading: boolean;
  actionSubmitting: ProviderDialogAction | null;
  onConnect: (form: ProviderDialogFormValues) => Promise<void>;
  onReject: () => Promise<void>;
}) {
  const [connectorName, setConnectorName] = useState(dialog.form.connectorName);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [scopes, setScopes] = useState(dialog.form.scopes.join("\n"));
  const [copied, setCopied] = useState(false);
  const ready = !!connectorName.trim() && !!clientId.trim() && !!clientSecret.trim();
  const fieldPrefix = `provider-connector-${event.id}`;

  const copyRedirectUri = async () => {
    if (!dialog.form.redirectUri) return;
    try {
      await navigator.clipboard.writeText(dialog.form.redirectUri);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      setCopied(false);
    }
  };

  const submit = async (submitEvent: FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();
    if (disabled || resolved || !ready) return;
    await onConnect({
      name: connectorName.trim(),
      client_id: clientId.trim(),
      client_secret: clientSecret.trim(),
      scopes: scopes.split(/\r?\n/).map((scope) => scope.trim()).filter(Boolean),
    });
  };

  return (
    <div id={`activity-event-${event.id}`} className="scroll-mt-3 px-4">
      <ActivityTimestamp value={event.observed_at} />
      <section className="border-l-2 border-l-blue-500 bg-slate-50 py-3 pl-4 pr-3">
        <h2 className="text-sm font-medium leading-5 text-slate-950">{dialog.title}</h2>
        {dialog.description && (
          <p className="mt-2 text-sm leading-5 text-slate-600">{dialog.description}</p>
        )}

        <form className="mt-3 space-y-3" onSubmit={submit}>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm leading-5 text-amber-900">
              Add this URL to your OAuth provider&apos;s allowed redirect URIs before saving the Client ID and Secret below.
            </p>
            {loading && !dialog.form.redirectUri ? (
              <div className="mt-2 h-9 animate-pulse rounded-md border border-amber-200 bg-white/70" aria-label="Loading redirect URI" />
            ) : dialog.form.redirectUri ? (
              <div className="mt-2 flex gap-2">
                <Input
                  value={dialog.form.redirectUri}
                  readOnly
                  aria-label="OAuth redirect URI"
                  className="h-9 bg-white font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0 bg-white"
                  onClick={() => { void copyRedirectUri(); }}
                  aria-label={copied ? "Redirect URI copied" : "Copy redirect URI"}
                  title={copied ? "Copied" : "Copy redirect URI"}
                >
                  <Copy className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-amber-800">The redirect URI is temporarily unavailable.</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${fieldPrefix}-name`} className="text-sm font-medium">
              Connector name <span className="text-blue-600">*</span>
            </Label>
            <Input
              id={`${fieldPrefix}-name`}
              value={connectorName}
              onChange={(inputEvent) => setConnectorName(inputEvent.target.value)}
              disabled={disabled || resolved}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${fieldPrefix}-client-id`} className="text-sm font-medium">
              Client ID <span className="text-blue-600">*</span>
            </Label>
            <Input
              id={`${fieldPrefix}-client-id`}
              value={clientId}
              onChange={(inputEvent) => setClientId(inputEvent.target.value)}
              placeholder="Enter OAuth client ID"
              autoComplete="off"
              disabled={disabled || resolved}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${fieldPrefix}-client-secret`} className="text-sm font-medium">
              Client Secret <span className="text-blue-600">*</span>
            </Label>
            <Input
              id={`${fieldPrefix}-client-secret`}
              type="password"
              value={clientSecret}
              onChange={(inputEvent) => setClientSecret(inputEvent.target.value)}
              placeholder="Enter OAuth client secret"
              autoComplete="new-password"
              disabled={disabled || resolved}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${fieldPrefix}-scopes`} className="text-sm font-normal">Scopes</Label>
            <p className="text-sm leading-5 text-slate-500">
              Choose the scopes your app needs, or add your own below.
            </p>
            <Textarea
              id={`${fieldPrefix}-scopes`}
              value={scopes}
              onChange={(inputEvent) => setScopes(inputEvent.target.value)}
              rows={5}
              disabled={disabled || resolved}
              className="min-h-[6.5rem] bg-white text-sm"
            />
          </div>

          {resolved ? (
            <p className="text-xs leading-5 text-slate-600">Completed.</p>
          ) : (
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" disabled={disabled || !ready}>
                {actionSubmitting === "submit_form" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Connect
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => { void onReject(); }}
              >
                {actionSubmitting === "reject" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Reject
              </Button>
            </div>
          )}
        </form>
      </section>
    </div>
  );
}

function ProviderUiValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return <p className="break-words text-xs leading-5 text-slate-600">{String(value)}</p>;
  }
  if (depth >= 6) return <p className="text-xs text-slate-500">Additional details available.</p>;
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1.5 pl-4 text-xs leading-5 text-slate-600">
        {value.slice(0, 50).map((item, index) => <li key={index} className="list-disc"><ProviderUiValue value={item} depth={depth + 1} /></li>)}
      </ul>
    );
  }
  const record = asRecord(value);
  return (
    <dl className="space-y-2">
      {Object.entries(record).map(([label, child]) => (
        <div key={label} className="rounded-md bg-slate-50 px-3 py-2">
          <dt className="mb-0.5 text-xs font-medium text-slate-800">{label.replace(/[_-]+/g, " ")}</dt>
          <dd><ProviderUiValue value={child} depth={depth + 1} /></dd>
        </div>
      ))}
    </dl>
  );
}

function ProviderUiEvent({
  event,
  panel,
  actionable,
  resolved,
  submitting,
  onAction,
}: {
  event: OperatingSystemJobEvent;
  panel: ProviderUiDescription;
  actionable: boolean;
  resolved: boolean;
  submitting: boolean;
  onAction: (eventId: string, actionRef: string, input: Record<string, unknown>) => Promise<boolean>;
}) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [submittingRef, setSubmittingRef] = useState<string | null>(null);
  const state = providerDialogState(event) ?? panel.state;
  const isResolved = resolved || ["answered", "dismissed", "closed"].includes(state);
  const title = typeof panel.display.title === "string" ? panel.display.title
    : typeof panel.display.heading === "string" ? panel.display.heading
    : event.content || "Provider request";
  const description = typeof panel.display.description === "string" ? panel.display.description
    : typeof panel.display.summary === "string" ? panel.display.summary : null;
  const rest = Object.fromEntries(Object.entries(panel.display).filter(([key]) => !["title", "heading", "description", "summary"].includes(key)));
  const disabled = submitting || !!submittingRef || !actionable || isResolved;
  const titleId = `provider-ui-${event.id}`;
  return (
    <div id={`activity-event-${event.id}`} className="scroll-mt-3 px-4">
      <ActivityTimestamp value={event.observed_at} />
      <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" aria-labelledby={titleId}>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"><ShieldCheck className="h-4 w-4" aria-hidden="true" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2"><h2 id={titleId} className="text-sm font-semibold leading-5 text-slate-950">{title}</h2>
              {!isResolved && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">Action required</span>}
            </div>
            {description && <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p>}
          </div>
        </div>
        {Object.keys(rest).length > 0 && <div className="mt-3"><ProviderUiValue value={rest} /></div>}
        {isResolved ? <p className="mt-3 text-xs leading-5 text-slate-600">{state === "dismissed" ? "Skipped." : "Completed."}</p>
          : !actionable ? <p className="mt-3 text-xs leading-5 text-slate-600">This request is no longer awaiting a response.</p> : null}
        {!isResolved && actionable && panel.actions.map((action) => (
          <div key={action.ref} className="mt-3 rounded-md border border-slate-100 p-2.5">
            {action.input.map((field) => (
              <label key={field.name} className="mb-2 block text-xs font-medium text-slate-700">{field.label}
                {field.type === "textarea" ? <textarea className="mt-1 w-full rounded border border-slate-200 p-2 text-sm" value={String(values[field.name] ?? "")} onChange={(e) => setValues((current) => ({ ...current, [field.name]: e.target.value }))} />
                  : field.type === "boolean" ? <input className="ml-2" type="checkbox" checked={values[field.name] === true} onChange={(e) => setValues((current) => ({ ...current, [field.name]: e.target.checked }))} />
                  : field.type === "select" ? <select className="mt-1 w-full rounded border border-slate-200 p-2 text-sm" value={String(values[field.name] ?? "")} onChange={(e) => setValues((current) => ({ ...current, [field.name]: e.target.value }))}>{field.options.map((option) => <option key={option} value={option}>{option}</option>)}</select>
                  : <input className="mt-1 w-full rounded border border-slate-200 p-2 text-sm" type={field.type === "number" ? "number" : "text"} value={String(values[field.name] ?? "")} onChange={(e) => setValues((current) => ({ ...current, [field.name]: field.type === "number" ? Number(e.target.value) : e.target.value }))} />}
              </label>
            ))}
            <Button type="button" size="sm" variant={action.tone === "secondary" ? "outline" : action.tone === "destructive" ? "destructive" : "default"} disabled={disabled} onClick={() => {
              setSubmittingRef(action.ref);
              void onAction(event.id, action.ref, values).finally(() => setSubmittingRef(null));
            }}>{submittingRef === action.ref && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}{action.label}</Button>
          </div>
        ))}
      </section>
    </div>
  );
}

function ProviderDialogEvent({
  event,
  actionable,
  resolved,
  submitting,
  onLoad,
  onAction,
  onRetryAuthorization,
  canRetryAuthorization,
  job,
}: {
  event: OperatingSystemJobEvent;
  actionable: boolean;
  resolved: boolean;
  submitting: boolean;
  onLoad: (eventId: string) => Promise<ProviderDialogDescription | null>;
  onAction: (
    eventId: string,
    action: ProviderDialogAction,
    form?: ProviderDialogFormValues,
  ) => Promise<boolean>;
  onRetryAuthorization: (serviceName: string) => Promise<boolean>;
  canRetryAuthorization: boolean;
  job: OperatingSystemJob | null;
}) {
  const publicDialog = providerDialogProjection(event);
  const state = providerDialogState(event) ?? "closed";
  const [loadedDialog, setLoadedDialog] = useState<ProviderDialogDescription | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [actionSubmitting, setActionSubmitting] = useState<ProviderDialogAction | null>(null);
  const [retrySubmitting, setRetrySubmitting] = useState(false);
  const [resolvedLocally, setResolvedLocally] = useState(false);
  const actionInFlightRef = useRef(false);
  const dialog = loadedDialog ?? publicDialog;

  useEffect(() => {
    if (!actionable || resolved || ["answered", "dismissed", "closed"].includes(state)) return;
    let active = true;
    setDialogLoading(true);
    void onLoad(event.id).then((description) => {
      if (active && description) setLoadedDialog(description);
    }).finally(() => {
      if (active) setDialogLoading(false);
    });
    return () => {
      active = false;
    };
  }, [actionable, event.id, onLoad, resolved, state]);

  if (!dialog) return null;

  const isResolved = resolved || resolvedLocally || ["answered", "dismissed", "closed"].includes(state);
  const interruptedAuthorization = dialog.kind === "oauth" && !isResolved && !actionable &&
    job?.id === event.job_id && job.status === "failed";
  const rawServiceName = asString(asRecord(event.public_metadata).service_name, 80) ??
    dialog.primaryActionLabel?.replace(/^Connect\s+/i, "") ??
    dialog.title.replace(/^Connect\s+/i, "");
  const serviceName = rawServiceName
    .replace(/[^A-Za-z0-9 .&()+-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80) || "account";
  const disabled = submitting || !!actionSubmitting || !actionable || isResolved;
  const authorizingOAuth = dialog.kind === "oauth" && state === "authorizing";
  const primaryAction: ProviderDialogAction = dialog.kind === "oauth" ? "begin_oauth"
    : "approve";
  const primaryLabel = authorizingOAuth
    ? "Reopen secure window"
    : dialog.primaryActionLabel;

  const submitAction = async (
    action: ProviderDialogAction,
    form?: ProviderDialogFormValues,
  ) => {
    if (isResolved || !actionable || actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setActionSubmitting(action);
    let accepted = false;
    try {
      accepted = await onAction(event.id, action, form);
      if (accepted) setResolvedLocally(true);
    } catch {
      // The parent shows a retryable error and keeps the dialog actionable.
    } finally {
      setActionSubmitting(null);
      if (!accepted) actionInFlightRef.current = false;
    }
  };
  const titleId = `provider-dialog-${event.id}`;

  if (dialog.kind === "form" && dialog.form) {
    return (
      <ProviderConnectorFormEvent
        event={event}
        dialog={dialog}
        disabled={disabled}
        resolved={isResolved}
        loading={dialogLoading}
        actionSubmitting={actionSubmitting}
        onConnect={(form) => submitAction("submit_form", form)}
        onReject={() => submitAction("reject")}
      />
    );
  }

  return (
    <div key={event.id} id={`activity-event-${event.id}`} className="scroll-mt-3 px-4">
      <ActivityTimestamp value={event.observed_at} />
      <section
        className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
        aria-labelledby={titleId}
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={titleId} className="text-sm font-semibold leading-5 text-slate-950">
                {dialog.title}
              </h2>
              {!isResolved && !interruptedAuthorization && (
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                  Action required
                </span>
              )}
            </div>
            {dialog.description && (
              <p className="mt-1 text-xs leading-5 text-slate-600">{dialog.description}</p>
            )}
          </div>
        </div>

        {dialog.permissions.length > 0 && (
          <div className="mt-3 rounded-md bg-slate-50 px-3 py-2.5">
            <p className="text-xs font-medium text-slate-800">Requested permissions</p>
            <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
              {dialog.permissions.map((permission) => <li key={permission}>{permission}</li>)}
            </ul>
          </div>
        )}

        {dialog.details.length > 0 && (
          <dl className="mt-3 space-y-1.5 text-xs leading-5">
            {dialog.details.map((detail) => (
              <div key={`${detail.label}:${detail.value}`} className="grid grid-cols-[minmax(0,7rem)_1fr] gap-2">
                <dt className="font-medium text-slate-700">{detail.label}</dt>
                <dd className="break-words text-slate-600">{detail.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {isResolved ? (
          <p className="mt-3 text-xs leading-5 text-slate-600">
            {state === "dismissed" ? "Skipped." : "Completed."}
          </p>
        ) : interruptedAuthorization ? (
          <p className="mt-3 text-xs font-medium leading-5 text-amber-800">Setup not finished</p>
        ) : state === "authorizing" ? (
          <p className="mt-3 text-xs leading-5 text-slate-600" role="status">
            Finish setup in the secure window. If it was closed or blocked, reopen it, then continue setup.
          </p>
        ) : !dialog.canApprove ? (
          <p className="mt-3 text-xs leading-5 text-slate-600">
            This request is visible for transparency, but it cannot be completed safely from this screen.
          </p>
        ) : null}

        {!isResolved && actionable ? (
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            {dialog.canApprove && primaryLabel && (
              <Button
                type="button"
                size="sm"
                disabled={disabled}
                onClick={() => { void submitAction(primaryAction); }}
              >
                {actionSubmitting === primaryAction
                  ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : dialog.kind === "oauth"
                  ? <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  : null}
                {primaryLabel}
              </Button>
            )}
            {authorizingOAuth && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => { void submitAction("poll_oauth"); }}
              >
                {actionSubmitting === "poll_oauth" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Check setup
              </Button>
            )}
            {dialog.canReject && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => { void submitAction("reject"); }}
              >
                {actionSubmitting === "reject" && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Not now
              </Button>
            )}
          </div>
        ) : interruptedAuthorization && canRetryAuthorization ? (
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={submitting || retrySubmitting}
              onClick={() => {
                if (retrySubmitting) return;
                setRetrySubmitting(true);
                void onRetryAuthorization(serviceName).finally(() => setRetrySubmitting(false));
              }}
            >
              {retrySubmitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Continue setup
            </Button>
          </div>
        ) : !isResolved && !actionable ? (
          <p className="mt-3 text-xs leading-5 text-slate-600">This request is no longer awaiting a response.</p>
        ) : null}
      </section>
    </div>
  );
}

function renderMessageEvent(
  event: OperatingSystemJobEvent,
  options: {
    canApply: boolean;
    canApplyRouterClarification: boolean;
    canAnswerProviderClarification: boolean;
    actionableProviderClarificationEventId: string | null;
    actionableProviderDialogEventId: string | null;
    submitting: boolean;
    onClarifyingAnswer: (eventId: string, answers: ProviderClarificationAnswer[]) => Promise<boolean>;
    onProviderDialogLoad: (eventId: string) => Promise<ProviderDialogDescription | null>;
    onProviderDialogAction: (
      eventId: string,
      action: ProviderDialogAction,
      form?: ProviderDialogFormValues,
    ) => Promise<boolean>;
    onProviderUiAction: (eventId: string, actionRef: string, input: Record<string, unknown>) => Promise<boolean>;
    onRetryAuthorization: (serviceName: string) => Promise<boolean>;
    onSuggestion: (prompt: string) => void;
    onAddAgenticFlow: (
      prompt: string,
      reference: { discoveryId: string; candidateId: string; displayName: string },
    ) => void;
    onRouterClarificationAnswer: (decisionId: string, answer: string) => Promise<boolean>;
    resolvedProviderDialogEventIds: Set<string>;
    answeredProviderClarificationEventIds: Set<string>;
    answeredRouterClarificationDecisionIds: Set<string>;
    activeAgenticFlowDiscoveryId: string | null;
    actionableRouterClarificationDecisionId: string | null;
    routerClarificationAssociationLoadingEventIds: Set<string>;
    job: OperatingSystemJob | null;
    historyEvents: readonly OperatingSystemJobEvent[];
  },
) {
  const content = displayEventContent(event);
  if (!content) return null;

  if (event.event_type === "clarifying_questions") {
    return <ProviderClarifyingQuestionEvent
      key={event.id}
      event={event}
      actionable={options.canAnswerProviderClarification && options.actionableProviderClarificationEventId === event.id}
      answered={options.answeredProviderClarificationEventIds.has(event.id)}
      submitting={options.submitting}
      onAnswer={options.onClarifyingAnswer}
    />;
  }

  if (event.event_type === "provider_dialog") {
    const providerUi = providerUiProjection(event);
    if (providerUi) {
      return <ProviderUiEvent key={event.id} event={event} panel={providerUi}
        actionable={options.canAnswerProviderClarification && options.actionableProviderDialogEventId === event.id}
        resolved={options.resolvedProviderDialogEventIds.has(event.id)} submitting={options.submitting}
        onAction={options.onProviderUiAction} />;
    }
    return (
      <ProviderDialogEvent
        key={event.id}
        event={event}
        actionable={options.canAnswerProviderClarification && options.actionableProviderDialogEventId === event.id}
        resolved={options.resolvedProviderDialogEventIds.has(event.id)}
        submitting={options.submitting}
        onLoad={options.onProviderDialogLoad}
        onAction={options.onProviderDialogAction}
        onRetryAuthorization={options.onRetryAuthorization}
        canRetryAuthorization={options.canApply}
        job={options.job}
      />
    );
  }

  if (event.event_type === "router_clarification") {
    const decisionId = routerDecisionId(event);
    return (
      <RouterClarificationEvent
        key={event.id}
        event={event}
        actionable={!!decisionId && options.actionableRouterClarificationDecisionId === decisionId}
        associationLoading={options.routerClarificationAssociationLoadingEventIds.has(event.id)}
        canApply={options.canApplyRouterClarification}
        submitting={!!decisionId && options.actionableRouterClarificationDecisionId === decisionId && options.submitting}
        answered={options.answeredRouterClarificationDecisionIds.has(decisionId ?? "")}
        onAnswer={options.onRouterClarificationAnswer}
      />
    );
  }

  if (event.event_type === "agentic_flow_suggestions") {
    const candidates = agenticFlowSuggestions(event);
    if (!candidates.length) return null;
    const readinessLabel: Record<AgenticFlowSuggestion["readiness"], string> = {
      ready_to_build: "Ready to add",
      generatable: "Automatic setup available",
      needs_data: "Needs data",
      needs_integration: "Needs a connection",
      needs_adapter: "Automatic setup required",
    };
    return (
      <section key={event.id} id={`activity-event-${event.id}`} className="scroll-mt-3 px-4" aria-label="Execution ideas">
        <ActivityTimestamp value={event.observed_at} />
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-sm font-semibold leading-5 text-slate-950">{content}</p>
          <div className="mt-3 space-y-3">
            {candidates.map((candidate, index) => (
              <article key={candidate.candidateId} className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-500">Suggestion {index + 1}</p>
                    <h3 className="mt-0.5 text-sm font-semibold leading-5 text-slate-950">{candidate.shortName}</h3>
                  </div>
                  <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600">{readinessLabel[candidate.readiness]}</span>
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-700">{candidate.whatItDoes}</p>
                <p className="mt-2 text-xs leading-5 text-slate-600"><span className="font-medium text-slate-800">Why useful:</span> {candidate.whyUseful}</p>
                <p className="mt-1 text-xs leading-5 text-slate-600"><span className="font-medium text-slate-800">Looks at:</span> {candidate.observes.join(", ")}</p>
                <p className="mt-1 text-xs leading-5 text-slate-600"><span className="font-medium text-slate-800">Produces:</span> {candidate.result}</p>
                {candidate.missingRequirement && <p className="mt-2 text-xs leading-5 text-amber-700">{candidate.missingRequirement}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={!options.canApply || options.submitting || !candidate.discoveryId || candidate.discoveryId !== options.activeAgenticFlowDiscoveryId || !!options.actionableRouterClarificationDecisionId || !["ready_to_build", "generatable", "needs_adapter"].includes(candidate.readiness)} className="rounded-md bg-slate-950 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300" onClick={() => candidate.discoveryId && options.onAddAgenticFlow(candidate.selectionPrompt, { discoveryId: candidate.discoveryId, candidateId: candidate.candidateId, displayName: candidate.shortName })}>
                    {candidate.readiness === "ready_to_build" ? "Add this flow" : ["generatable", "needs_adapter"].includes(candidate.readiness) ? "Add with automatic setup" : readinessLabel[candidate.readiness]}
                  </button>
                  <button type="button" disabled={!options.canApply || options.submitting} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" onClick={() => options.onSuggestion(`Tell me more about the “${candidate.shortName}” flow.`)}>
                    Tell me more
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (event.event_type === "suggestions") {
    const suggestions = providerEditSuggestions([event]);
    if (suggestions.length === 0) return null;
    return (
      <section
        key={event.id}
        id={`activity-event-${event.id}`}
        className="scroll-mt-3 px-4"
        aria-label="Suggested App changes"
      >
        <ActivityTimestamp value={event.observed_at} />
        <p className="mb-2 text-xs font-medium text-slate-700">Suggestions</p>
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.prompt}
              type="button"
              disabled={!options.canApply || options.submitting}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => options.onSuggestion(suggestion.prompt)}
              title={suggestion.prompt}
            >
              {suggestion.title}
            </button>
          ))}
        </div>
      </section>
    );
  }

  if (isCreditEvent(event) && /out of|0 credits|credit/i.test(content)) {
    const title = content.split("\n")[0] || "You're out of daily credits on your Free plan";
    return (
      <div key={event.id} className="px-4">
        <div className="text-center text-[11px] text-slate-300">{formatEventTime(event.observed_at)}</div>
        <div className="mt-3 text-center">
          <p className="text-sm leading-5 text-slate-900">{title}</p>
        </div>
      </div>
    );
  }

  const userMessage = isUserEvent(event) || isVisiblePostCompletionProviderUserMessage(
    event,
    options.job,
    options.historyEvents,
  );
  const showFullIntegrationInstruction = event.event_type === "change_request" &&
    SHARED_INTEGRATION_INSTRUCTIONS.has(content);
  if (userMessage) {
    return (
      <div key={event.id} className="px-4">
        <div className="ml-8 rounded-xl rounded-tr-sm bg-slate-200/65 px-3 py-2.5">
          <MessageContent content={content} collapsible={!showFullIntegrationInstruction} className="" />
          <MessageAttachments event={event} />
          {asRecord(event.public_metadata).app_message_receipt_id && (
            <p role="status" className="mt-1 text-xs leading-5 text-slate-600">
              {asRecord(event.public_metadata).delivery_state === "accepted" ? "Delivered"
                : asRecord(event.public_metadata).delivery_state === "sending" ? "Sending…"
                : providerText(asString(asRecord(event.public_metadata).delivery_message, 500)) || "Delivery unconfirmed"}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (event.event_type === "workspace_activity") {
    return (
      <div key={event.id} id={`activity-event-${event.id}`} className="scroll-mt-3 px-4">
        <ActivityTimestamp value={event.observed_at} />
        <div className="space-y-2">
          {toolRows(content).map((row, index) => (
            <div key={`${event.id}-${index}`} className="flex items-center gap-2 text-sm text-slate-900">
              <FileText className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
              <span>{row.action}</span>
              <span className="rounded bg-slate-200/80 px-1.5 py-0.5 text-xs text-slate-600">{row.target}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (event.event_type === "history_sync_empty" || event.event_type === "credit_sync_empty" || event.event_type === "history_sync_failed") {
    return (
      <div key={event.id} className="px-4">
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-xs leading-5 text-slate-600 shadow-sm">
          {content}
        </div>
      </div>
    );
  }

  if (event.source === "base44_editor" && event.event_type === "editor_message") {
    return (
      <div key={event.id} className="px-4">
        <ActivityTimestamp value={event.observed_at} />
        <ProviderMarkdownContent content={content} />
      </div>
    );
  }

  return (
    <div key={event.id} className="px-4">
      <ActivityTimestamp value={event.observed_at} />
      <MessageContent content={content} collapsible={userMessage} className="" />
    </div>
  );
}

export function Base44MessagesPanel({
  statusDescriptor: suppliedDescriptor,
  app,
  job,
  active,
  progress,
  historyEvents,
  historyLoading,
  historySyncing,
  historyError,
  changeRequest,
  changeError,
  submitting,
  canApply,
  canApplyRouterClarification,
  canAnswerProviderClarification,
  providerInputWaiting,
  unavailableReason,
  onHide,
  onRefresh,
  onChangeRequest,
  attachments,
  onAttachmentsChange,
  onOpenIntegrations,
  onClarifyingAnswer,
  onProviderDialogLoad,
  onProviderDialogAction,
  onProviderUiAction,
  onRetryAuthorization,
  independentMessaging = false,
  onProviderMessageReply,
  onRouterClarificationAnswer,
  onSubmit,
  loadAgenticFlowList,
  loadAgenticFlowDetail,
  loadAgenticFlowDebug,
  runAgenticFlow,
  recurrenceAction,
  checkAgenticSetup,
  mutateAgenticFlow,
  isPlatformAdmin,
  onDiscoverAgenticFlows,
  onAddAgenticFlow,
  onTargetAgenticFlowModification,
}: {
  statusDescriptor?: AppStatusDescriptor;
  app: OperatingSystemApp;
  job: OperatingSystemJob | null;
  active: boolean;
  progress: number;
  historyEvents: OperatingSystemJobEvent[];
  historyLoading: boolean;
  historySyncing: boolean;
  historyError: string | null;
  changeRequest: string;
  changeError: string | null;
  submitting: boolean;
  canApply: boolean;
  canApplyRouterClarification: boolean;
  canAnswerProviderClarification: boolean;
  providerInputWaiting: boolean;
  unavailableReason: string | null;
  onHide: () => void;
  onRefresh: () => void;
  onChangeRequest: (value: string) => void;
  attachments: File[];
  onAttachmentsChange: (files: File[]) => void;
  onOpenIntegrations: () => void;
  onClarifyingAnswer: (eventId: string, answers: ProviderClarificationAnswer[]) => Promise<boolean>;
  onProviderDialogLoad: (eventId: string) => Promise<ProviderDialogDescription | null>;
  onProviderDialogAction: (
    eventId: string,
    action: ProviderDialogAction,
    form?: ProviderDialogFormValues,
  ) => Promise<boolean>;
  onProviderUiAction: (eventId: string, actionRef: string, input: Record<string, unknown>) => Promise<boolean>;
  onRetryAuthorization: (serviceName: string) => Promise<boolean>;
  independentMessaging?: boolean;
  onProviderMessageReply: (eventId: string, reply: string) => Promise<boolean>;
  onRouterClarificationAnswer: (decisionId: string, answer: string) => Promise<boolean>;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  loadAgenticFlowList: (includeArchived?: boolean) => Promise<AgenticFlowListResponse>;
  loadAgenticFlowDetail: (flowId: string) => Promise<AgenticFlowProductDetail>;
  loadAgenticFlowDebug: (flowId: string) => Promise<AgenticFlowDebugDetail>;
  runAgenticFlow: (flowId: string) => Promise<void>;
  recurrenceAction: (flowId: string, action: "test" | "enable_recurrence" | "recover_recurrence", actionVersion: string) => Promise<void>;
  checkAgenticSetup: (flowId: string, actionVersion: string) => Promise<void>;
  mutateAgenticFlow: (flowId: string, action: "pause" | "resume" | "archive") => Promise<void>;
  isPlatformAdmin: boolean;
  onDiscoverAgenticFlows: () => void;
  onAddAgenticFlow: (
    prompt: string,
    reference: { discoveryId: string; candidateId: string; displayName: string },
  ) => void;
  onTargetAgenticFlowModification: (flowId: string, displayName: string) => void;
}) {
  const [panelSection, setPanelSection] = useState<"activity" | "flows">("activity");
  const agenticUiAvailable = isAgenticUiAvailableHere();
  const visiblePanelSection = visibleWorkspaceSection(panelSection, agenticUiAvailable);
  const appStatus = suppliedDescriptor ?? getAppStatusDescriptor({ app, job });
  const reviewHold = !independentMessaging && job?.id === app.latest_job_id && isSupportManagedAppRecovery(job);
  const [attachmentDropActive, setAttachmentDropActive] = useState(false);
  const [panelWidth, setPanelWidth] = useState(ACTIVITY_PANEL_DEFAULT_WIDTH);
  const [resizing, setResizing] = useState(false);
  const [awayFromLatest, setAwayFromLatest] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentDragDepthRef = useRef(0);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const transcriptContentRef = useRef<HTMLDivElement>(null);
  const autoFollowRef = useRef<ReturnType<typeof createTranscriptAutoFollow> | null>(null);
  if (!autoFollowRef.current) autoFollowRef.current = createTranscriptAutoFollow(setAwayFromLatest);
  const pointerDownRef = useRef(false);
  const touchStartYRef = useRef<number | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const resizeStartRef = useRef<{ clientX: number; width: number } | null>(null);
  const sortedEvents = [...historyEvents].sort((a, b) => {
    const timeDifference = new Date(a.observed_at).getTime() - new Date(b.observed_at).getTime();
    if (timeDifference) return timeDifference;
    const leftSourceOrder = eventSourceOrder(a);
    const rightSourceOrder = eventSourceOrder(b);
    if (leftSourceOrder !== null && rightSourceOrder !== null && leftSourceOrder !== rightSourceOrder) {
      return leftSourceOrder - rightSourceOrder;
    }
    const rankDifference = eventSortRank(a) - eventSortRank(b);
    if (rankDifference) return rankDifference;
    if (a.event_type === "tool_call" && b.event_type === "tool_call") {
      return (a.content ?? "").localeCompare(b.content ?? "");
    }
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
  const rawVisibleActivityEvents = latestProviderDialogEvents(sortedEvents.filter((event) =>
    (isVisibleActivityEvent(event) || isVisiblePostCompletionProviderUserMessage(event, job)) &&
    (!reviewHold || !["clarifying_questions", "router_clarification", "provider_dialog"].includes(event.event_type)) &&
    (agenticUiAvailable || !isAgenticFlowActivityEvent(event))
  ));
  const visibleActivityEvents = rawVisibleActivityEvents.filter((event, index, events) => {
    if (asRecord(event.public_metadata).app_message_receipt_id) return true;
    if (!["change_request", "router_request", "router_response", "editor_message"].includes(event.event_type)) {
      return true;
    }
    const content = displayEventContent(event).replace(/\s+/g, " ").trim().toLowerCase();
    const observedAt = new Date(event.observed_at).getTime();
    return !events.slice(0, index).some((candidate) => {
      if (
        !["change_request", "router_request", "router_response", "editor_message"].includes(candidate.event_type) ||
        (candidate.source === event.source && candidate.event_type === event.event_type)
      ) {
        return false;
      }
      const candidateContent = displayEventContent(candidate).replace(/\s+/g, " ").trim().toLowerCase();
      const candidateTime = new Date(candidate.observed_at).getTime();
      return candidateContent === content && Number.isFinite(candidateTime) && Number.isFinite(observedAt) &&
        Math.abs(observedAt - candidateTime) <= 5 * 60 * 1000;
    });
  });
  const resolvedProviderDialogIds = resolvedProviderDialogEventIds(sortedEvents);
  const answeredProviderClarificationIds = answeredProviderClarificationEventIds(sortedEvents);
  const answeredRouterDecisionIds = answeredRouterClarificationDecisionIds(sortedEvents);
  const agenticFlowActivity = currentAgenticFlowActivity(sortedEvents);
  const activeJobId = app.latest_job_id;
  const activeJobCanAcceptProviderResponse = !!job &&
    !reviewHold &&
    job.id === activeJobId &&
    isActiveOsJobStatus(job.status);
  const routerClarificationAssociationStates = new Map(
    sortedEvents
      .filter((event) => event.event_type === "router_clarification")
      .map((event) => [event.id, routerClarificationAssociationState({
        event,
        events: sortedEvents,
        latestJobId: activeJobId,
        job,
      })]),
  );
  const actionableRouterClarificationDecisionId = !reviewHold && agenticFlowActivity.actionableClarificationDecisionId &&
    sortedEvents.some((event) =>
      routerClarificationAssociationStates.get(event.id) === "actionable" &&
      routerDecisionId(event) === agenticFlowActivity.actionableClarificationDecisionId,
    )
    ? agenticFlowActivity.actionableClarificationDecisionId
    : null;
  const routerClarificationAssociationLoadingEventIds = new Set(
    [...routerClarificationAssociationStates]
      .filter(([, state]) => state === "loading")
      .map(([eventId]) => eventId),
  );
  const actionableProviderClarificationEventId = activeJobCanAcceptProviderResponse
    ? latestWaitingProviderClarificationEvent(visibleActivityEvents, activeJobId)?.id ?? null
    : null;
  const actionableProviderDialogEventId = activeJobCanAcceptProviderResponse
    ? [...visibleActivityEvents].reverse().find((event) =>
      event.event_type === "provider_dialog" &&
      event.job_id === activeJobId &&
      !resolvedProviderDialogIds.has(event.id) &&
      ["waiting", "authorizing"].includes(providerDialogState(event) ?? "")
    )?.id ?? null
    : null;
  const actionableStructuredEventId = actionableProviderClarificationEventId ?? actionableProviderDialogEventId;
  const visibleEvents = groupWorkspaceActivityEvents(visibleActivityEvents);
  const repliedProviderMessageIds = repliedProviderMessageEventIds(sortedEvents);
  const latestCurrentJobProviderMessage = [...visibleActivityEvents].reverse().find((event) =>
    event.source === "base44_editor" &&
    event.event_type === "editor_message" &&
    event.role?.toLowerCase() === "assistant" &&
    !!activeJobId && event.job_id === activeJobId
  ) ?? null;
  const actionableProviderMessageEventId =
      !actionableProviderClarificationEventId && !actionableProviderDialogEventId &&
      latestCurrentJobProviderMessage &&
      providerMessageRequestsReply(latestCurrentJobProviderMessage.content) &&
      !repliedProviderMessageIds.has(latestCurrentJobProviderMessage.id)
    ? latestCurrentJobProviderMessage.id
    : null;
  const canReplyToProviderMessage = !reviewHold && canAnswerProviderClarification && !!actionableProviderMessageEventId;
  // A structured clarification keeps its guided choices, while the composer
  // remains available as the Provider's alternate free-form response path.
  // This is intentionally limited to the newest waiting question on the
  // active job; stale and answered questions must not reopen the composer.
  const canReplyToProviderInput = !reviewHold && (canReplyToProviderMessage ||
    (canAnswerProviderClarification && !!actionableProviderClarificationEventId));
  const latestActivity = visibleActivityEvents[visibleActivityEvents.length - 1];
  const activityCursor = latestActivity
    ? `${visibleActivityEvents.length}:${latestActivity.id}:${latestActivity.content ?? ""}`
    : "";
  const firstActivityId = visibleEvents[0]?.id ?? null;
  const hasCreditBlock = !independentMessaging && hasBlockingCreditEvent(sortedEvents);
  const controlsDisabled = (submitting && !independentMessaging) || hasCreditBlock || reviewHold || (!canApply && !canReplyToProviderInput);
  const composerBlocked = hasCreditBlock || reviewHold || (!canApply && !canReplyToProviderInput);
  const attachmentControlsDisabled = true; // Text-only transparent conversation.
  const composerWasDisabledRef = useRef(controlsDisabled);
  const previousProviderMessageIdRef = useRef(latestCurrentJobProviderMessage?.id ?? null);
  const shortcutHintId = `app-composer-shortcut-${app.id}`;
  const statusLabel = independentMessaging ? submitting ? "Sending" : "App conversation" : hasCreditBlock
    ? "No credits remaining"
    : reviewHold
    ? job?.job_type === "update" ? "Update could not be verified" : "App could not be verified"
    : actionableStructuredEventId
    ? "Action required"
    : canReplyToProviderInput
    ? "Waiting for reply"
    : active ? job?.current_step === "settling_message_usage" ? "Finalizing message" : "Message in progress" : appStatus.label;
  const inputPlaceholder = hasCreditBlock
    ? "0 credits remaining"
    : canReplyToProviderInput
    ? "Reply to continue this App update"
    : actionableStructuredEventId
    ? "Complete the required action above to continue"
    : canApply
    ? "Ask a question or describe a change"
    : providerText(unavailableReason) || "Requests will be available when this App is ready.";

  useEffect(() => {
    const storedWidth = Number(window.localStorage.getItem(ACTIVITY_PANEL_WIDTH_STORAGE_KEY));
    if (Number.isFinite(storedWidth)) {
      setPanelWidth(clampActivityPanelWidth(storedWidth, window.innerWidth));
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(ACTIVITY_PANEL_WIDTH_STORAGE_KEY, String(panelWidth));
  }, [panelWidth]);

  useEffect(() => {
    const constrainPanelWidth = () => {
      setPanelWidth((current) => clampActivityPanelWidth(current, window.innerWidth));
    };
    window.addEventListener("resize", constrainPanelWidth);
    return () => window.removeEventListener("resize", constrainPanelWidth);
  }, []);

  useLayoutEffect(() => {
    autoFollowRef.current?.reset();
  }, [app.id]);

  useLayoutEffect(() => {
    const transcript = transcriptRef.current;
    if (transcript) autoFollowRef.current?.contentChanged(transcript, firstActivityId);
  }, [app.id, activityCursor, firstActivityId, historyLoading]);

  // Follow content and viewport resizing only while the reader is at the end.
  useEffect(() => {
    const transcript = transcriptRef.current;
    const content = transcriptContentRef.current;
    if (!transcript || !content) return;
    const observer = new ResizeObserver(() => {
      autoFollowRef.current?.resized(transcript);
    });
    observer.observe(transcript);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const endPointerScroll = () => { pointerDownRef.current = false; };
    window.addEventListener("pointerup", endPointerScroll);
    window.addEventListener("pointercancel", endPointerScroll);
    return () => {
      window.removeEventListener("pointerup", endPointerScroll);
      window.removeEventListener("pointercancel", endPointerScroll);
    };
  }, []);

  useLayoutEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;
    const resize = () => {
      composer.style.height = "48px";
      composer.style.height = `${Math.min(120, Math.max(48, composer.scrollHeight))}px`;
      composer.style.overflowY = composer.scrollHeight > 120 ? "auto" : "hidden";
    };
    resize();
    // Width changes can wrap the same text onto additional lines.
    let previousWidth = composer.clientWidth;
    const observer = new ResizeObserver(() => {
      if (composer.clientWidth === previousWidth) return;
      previousWidth = composer.clientWidth;
      resize();
    });
    observer.observe(composer);
    return () => observer.disconnect();
  }, [changeRequest, composerBlocked]);

  useLayoutEffect(() => {
    const becameAvailable = composerWasDisabledRef.current && !controlsDisabled;
    composerWasDisabledRef.current = controlsDisabled;
    const nextProviderMessageId = latestCurrentJobProviderMessage?.id ?? null;
    const receivedProviderMessage = nextProviderMessageId !== null &&
      nextProviderMessageId !== previousProviderMessageIdRef.current;
    previousProviderMessageIdRef.current = nextProviderMessageId;
    if ((!becameAvailable && !receivedProviderMessage) || controlsDisabled || changeError || !autoFollowRef.current?.isFollowing()) return;
    const frame = requestAnimationFrame(() => {
      if (!composerRef.current?.disabled) composerRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [changeError, controlsDisabled, latestCurrentJobProviderMessage?.id]);

  const addAttachments = useCallback((files: File[]) => {
    if (!attachmentControlsDisabled && files.length > 0) {
      onAttachmentsChange([...attachments, ...files]);
    }
  }, [attachmentControlsDisabled, attachments, onAttachmentsChange]);

  useEffect(() => {
    const clearDropState = () => {
      attachmentDragDepthRef.current = 0;
      setAttachmentDropActive(false);
    };
    if (attachmentControlsDisabled) clearDropState();
    const onDragEnter = (event: globalThis.DragEvent) => {
      if (!hasFileDragPayload(event.dataTransfer)) return;
      event.preventDefault();
      if (attachmentControlsDisabled) return;
      attachmentDragDepthRef.current += 1;
      setAttachmentDropActive(true);
    };
    const onDragOver = (event: globalThis.DragEvent) => {
      if (!hasFileDragPayload(event.dataTransfer)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = attachmentControlsDisabled ? "none" : "copy";
    };
    const onDragLeave = () => {
      if (attachmentDragDepthRef.current === 0) return;
      attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
      if (attachmentDragDepthRef.current === 0) setAttachmentDropActive(false);
    };
    const onDrop = (event: globalThis.DragEvent) => {
      const files = filesFromDrop(event.dataTransfer);
      if (!hasFileDragPayload(event.dataTransfer) && files.length === 0) return;
      event.preventDefault();
      clearDropState();
      addAttachments(files);
    };
    window.addEventListener("dragenter", onDragEnter, true);
    window.addEventListener("dragover", onDragOver, true);
    window.addEventListener("dragleave", onDragLeave, true);
    window.addEventListener("dragend", clearDropState, true);
    window.addEventListener("drop", onDrop, true);
    return () => {
      window.removeEventListener("dragenter", onDragEnter, true);
      window.removeEventListener("dragover", onDragOver, true);
      window.removeEventListener("dragleave", onDragLeave, true);
      window.removeEventListener("dragend", clearDropState, true);
      window.removeEventListener("drop", onDrop, true);
    };
  }, [addAttachments, attachmentControlsDisabled]);

  const resizePanel = (nextWidth: number) => {
    setPanelWidth(clampActivityPanelWidth(nextWidth, window.innerWidth));
  };

  const finishResize = () => {
    resizeStartRef.current = null;
    setResizing(false);
  };

  const handleResizeStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    resizeStartRef.current = { clientX: event.clientX, width: panelWidth };
    event.currentTarget.setPointerCapture(event.pointerId);
    setResizing(true);
  };

  const handleResizeMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = resizeStartRef.current;
    if (!start) return;
    resizePanel(start.width + event.clientX - start.clientX);
  };

  const handleResizeKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const maximum = activityPanelMaximumWidth(window.innerWidth);
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      resizePanel(panelWidth - 16);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      resizePanel(panelWidth + 16);
    } else if (event.key === "Home") {
      event.preventDefault();
      resizePanel(ACTIVITY_PANEL_MIN_WIDTH);
    } else if (event.key === "End") {
      event.preventDefault();
      resizePanel(maximum);
    }
  };

  const panelStyle = {
    "--activity-panel-width": `${panelWidth}px`,
  } as CSSProperties;

  return (
    <aside
      className={`relative flex min-h-0 w-full flex-col border-r border-[#d7dce4] text-slate-950 lg:w-[var(--activity-panel-width)] lg:shrink-0 ${resizing ? "select-none" : ""}`}
      style={{ background: PANEL_BG, ...panelStyle }}
      aria-label="Workspace activity panel"
    >
      <button
        type="button"
        className="absolute -right-1 top-0 z-20 hidden h-full w-2 cursor-col-resize touch-none items-center justify-center lg:flex focus-visible:outline-none"
        aria-label="Resize workspace activity panel"
        aria-orientation="vertical"
        aria-valuemin={ACTIVITY_PANEL_MIN_WIDTH}
        aria-valuemax={activityPanelMaximumWidth(typeof window === "undefined" ? ACTIVITY_PANEL_MAX_WIDTH + ACTIVITY_PANEL_MIN_PREVIEW_WIDTH : window.innerWidth)}
        aria-valuenow={panelWidth}
        role="separator"
        title="Drag to resize the activity panel"
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={finishResize}
        onPointerCancel={finishResize}
        onKeyDown={handleResizeKeyDown}
      >
        <span className={`h-12 w-1 rounded-full transition-colors ${resizing ? "bg-[#03876d]" : "bg-slate-300 hover:bg-slate-400"}`} />
      </button>
      {/* Panel head removed: App name, status and controls live in the page toolbar row. */}
      {agenticUiAvailable && (
        <div className="shrink-0 px-3 pb-3 pt-3">
          <div className="grid grid-cols-2 rounded-lg bg-slate-200/75 p-1" role="tablist" aria-label="App workspace tools">
            <button type="button" role="tab" aria-selected={visiblePanelSection === "activity"} onClick={() => setPanelSection("activity")} className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition ${visiblePanelSection === "activity" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              <MessageSquare className="h-3.5 w-3.5" />Activity
            </button>
            <button type="button" role="tab" aria-selected={panelSection === "flows"} onClick={() => setPanelSection("flows")} className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-medium transition ${panelSection === "flows" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              <Bot className="h-3.5 w-3.5" />Execution
            </button>
          </div>
        </div>
      )}

      {agenticUiAvailable && visiblePanelSection === "flows" ? (
        <AgenticFlowsPanel
          canManage={canApply}
          isAdmin={isPlatformAdmin}
          busy={submitting || active}
          loadList={loadAgenticFlowList}
          loadDetail={loadAgenticFlowDetail}
          loadDebug={loadAgenticFlowDebug}
          run={runAgenticFlow}
          recurrenceAction={recurrenceAction}
          checkSetup={checkAgenticSetup}
          mutate={mutateAgenticFlow}
          onDiscover={() => {
            setPanelSection("activity");
            onDiscoverAgenticFlows();
          }}
          onModify={(flow) => {
            setPanelSection("activity");
            onTargetAgenticFlowModification(flow.id, flow.name);
            requestAnimationFrame(() => composerRef.current?.focus());
          }}
        />
      ) : <>
      <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={transcriptRef}
        className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain pb-4"
        aria-label="Conversation history"
        tabIndex={0}
        onWheel={(event) => {
          if (event.deltaY < 0) autoFollowRef.current?.userScrollUp(event.currentTarget);
          else if (event.deltaY > 0) autoFollowRef.current?.userScrollDown();
        }}
        onTouchStart={(event) => { touchStartYRef.current = event.touches[0]?.clientY ?? null; }}
        onTouchMove={(event) => {
          const nextY = event.touches[0]?.clientY;
          if (nextY === undefined || touchStartYRef.current === null) return;
          if (nextY > touchStartYRef.current) autoFollowRef.current?.userScrollUp(event.currentTarget);
          else if (nextY < touchStartYRef.current) autoFollowRef.current?.userScrollDown();
          touchStartYRef.current = nextY;
        }}
        onPointerDown={() => { pointerDownRef.current = true; }}
        onPointerUp={() => { pointerDownRef.current = false; }}
        onPointerCancel={() => { pointerDownRef.current = false; }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey)) {
            autoFollowRef.current?.userScrollUp(event.currentTarget);
          } else if (['ArrowDown', 'PageDown', 'End'].includes(event.key) || event.key === ' ') {
            autoFollowRef.current?.userScrollDown();
          }
        }}
        onScroll={(event) => {
          autoFollowRef.current?.scrolled(event.currentTarget, pointerDownRef.current);
        }}
      >
        <div ref={transcriptContentRef} className="space-y-5">
          {historyLoading && visibleEvents.length === 0 ? (
            <div className="px-4 text-xs text-slate-500">
              <Loader2 className="mr-2 inline h-3.5 w-3.5 animate-spin" />
              Loading latest activity
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="px-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700 shadow-sm">
                {active
                  ? "Applyt is building your workspace. Updates will appear here as they arrive."
                  : "No edit requests or actionable build messages yet."}
              </div>
            </div>
          ) : (
            visibleEvents.map((event) => (
              <div key={event.id} data-activity-event-id={event.id} tabIndex={-1}>
                {renderMessageEvent(event, {
                  canApply,
                  canApplyRouterClarification: !reviewHold && canApplyRouterClarification,
                  canAnswerProviderClarification: !reviewHold && canAnswerProviderClarification,
                  actionableProviderClarificationEventId,
                  actionableProviderDialogEventId,
                  submitting,
                  onClarifyingAnswer,
                  onProviderDialogLoad,
      onProviderDialogAction,
      onProviderUiAction,
                  onRetryAuthorization,
                  onSuggestion: (prompt) => {
                    onChangeRequest(prompt);
                    requestAnimationFrame(() => composerRef.current?.focus());
                  },
                  onAddAgenticFlow,
                  onRouterClarificationAnswer,
                  resolvedProviderDialogEventIds: resolvedProviderDialogIds,
                  answeredProviderClarificationEventIds: answeredProviderClarificationIds,
                  answeredRouterClarificationDecisionIds: answeredRouterDecisionIds,
                  activeAgenticFlowDiscoveryId: agenticFlowActivity.activeDiscoveryId,
            actionableRouterClarificationDecisionId,
            routerClarificationAssociationLoadingEventIds,
                  job,
                  historyEvents: sortedEvents,
                })}
              </div>
            ))
          )}

          {historyError && (
            <div className="px-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
                {providerText(historyError)}
              </div>
            </div>
          )}

        </div>
      </div>
      {awayFromLatest && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white shadow-md"
          onClick={() => {
            if (transcriptRef.current) {
              autoFollowRef.current?.returnToLatest(transcriptRef.current);
              transcriptRef.current.focus({ preventScroll: true });
            }
          }}
        >
          Latest activity <ArrowDown className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
      </div>

      <form
        onSubmit={(event) => {
          if (independentMessaging) {
            onSubmit(event);
          } else if (actionableProviderClarificationEventId && canAnswerProviderClarification) {
            event.preventDefault();
            const reply = changeRequest.trim();
            if (!reply) return;
            void onClarifyingAnswer(actionableProviderClarificationEventId, [{
              questionIndex: 0,
              customText: reply,
            }]).then((accepted) => {
              if (accepted) onChangeRequest("");
            }).catch(() => {
              // The parent presents a retryable error and retains the draft.
            });
          } else if (canReplyToProviderMessage && actionableProviderMessageEventId) {
            event.preventDefault();
            void onProviderMessageReply(actionableProviderMessageEventId, changeRequest);
          } else {
            onSubmit(event);
          }
        }}
        className="shrink-0 px-3 pb-3"
      >
        {active && !reviewHold && !independentMessaging && (
          <div className="mb-2 overflow-hidden rounded-full bg-slate-200" aria-hidden="true">
            <div className="h-1.5 rounded-full bg-slate-500 transition-all" style={{ width: `${Math.max(5, Math.min(100, progress))}%` }} />
          </div>
        )}
        {changeError && (
          <p className="mb-2 px-2 text-sm leading-5 text-slate-700" role="alert">
            {providerText(changeError)}
          </p>
        )}
        {composerBlocked ? (
          <div className="flex h-14 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3" aria-label="Message availability">
            <span className="min-w-0 flex-1 truncate text-xs text-slate-600" title={inputPlaceholder}>{statusLabel}</span>
          </div>
        ) : (
        <div
          className={`relative rounded-xl border bg-white p-3 shadow-sm transition-colors ${attachmentDropActive
            ? "border-[#03876d] ring-2 ring-[#03876d]/20"
            : "border-slate-300"}`}
          aria-label="Message composer — drop files to attach"
        >
          {attachmentDropActive && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-[#03876d]/10 text-sm font-medium text-[#026c58]" aria-hidden="true">
              Drop files to attach
            </div>
          )}
          <p className="sr-only" role="status" aria-live="polite">
            {attachmentDropActive ? "Drop files to attach." : ""}
          </p>
          <input ref={attachmentInputRef} type="file" multiple className="sr-only" aria-label="Attach files" accept={documentAccept(true)}
            disabled={attachmentControlsDisabled}
            onChange={(event) => { addAttachments(Array.from(event.currentTarget.files ?? [])); event.currentTarget.value = ""; }} />
          <Textarea
            ref={composerRef}
            value={changeRequest}
            onChange={(event) => onChangeRequest(event.target.value)}
            onKeyDown={(event) => {
              if (!isComposerSubmitShortcut({
                key: event.key,
                shiftKey: event.shiftKey,
                isComposing: event.nativeEvent.isComposing,
                sendBlocked: reviewHold,
              })) return;
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }}
            disabled={controlsDisabled}
            placeholder={inputPlaceholder}
            rows={2}
            className="min-h-[48px] max-h-[120px] resize-none border-0 bg-transparent p-0 text-sm leading-6 shadow-none placeholder:text-slate-400 focus-visible:ring-0 disabled:cursor-not-allowed"
            aria-label={canReplyToProviderInput ? "Reply to App question" : "Ask a question or describe an App change"}
            aria-describedby={shortcutHintId}
            aria-keyshortcuts={reviewHold ? undefined : "Enter Shift+Enter"}
          />
          {attachments.length > 0 && <ul className="mt-2 max-h-24 space-y-1 overflow-y-auto" aria-label="Selected attachments">
            {attachments.map((file, index) => <li key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600">
              <Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 flex-1 truncate">{file.name}</span><span className="shrink-0 text-slate-400">{Math.ceil(file.size / 1024)} KB</span>
              <button type="button" aria-label={`Remove ${file.name}`} disabled={submitting} onClick={() => onAttachmentsChange(attachments.filter((_, current) => current !== index))}><X className="h-3.5 w-3.5" /></button>
            </li>)}
          </ul>}
          <div className="mt-2 flex items-center justify-between gap-2">
            <span id={shortcutHintId} className="whitespace-nowrap text-[11px] text-slate-400" title="Enter to send; Shift + Enter for a new line">
              <span aria-hidden="true">↵ to send</span><span className="sr-only">Enter to send. Shift + Enter for a new line.</span>
            </span>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-lg border-slate-300 bg-white text-slate-700 hover:bg-slate-50" disabled={attachmentControlsDisabled} onClick={() => attachmentInputRef.current?.click()} aria-label="Attach files (text messages only)" title="This conversation supports text messages only"><Paperclip className="h-3.5 w-3.5" /></Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 rounded-lg border-slate-300 bg-white px-2.5 text-xs text-slate-700 hover:bg-slate-50"
                onClick={onOpenIntegrations}
              >
                <Plug className="mr-1.5 h-3.5 w-3.5" />
                Integrations
              </Button>
              <Button
                type="submit"
                size="icon"
                className="h-8 w-8 rounded-lg bg-slate-400 hover:bg-slate-500 disabled:bg-slate-300"
                disabled={controlsDisabled}
                aria-label={canReplyToProviderInput ? "Send reply" : "Send App request"}
              >
                {submitting
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : canApply || canReplyToProviderInput
                  ? <Send className="h-4 w-4" />
                  : <Lock className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
        )}
      </form>
      </>}
    </aside>
  );
}
